import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-toastify";
import "./RawMaterials.scss";
import AccessDenied from "../../AccessDenied";
import { useAuth } from "../../../contexts/AuthContext";

// Organized units by category for better UX
const UNIT_CATEGORIES = {
  "Length / Distance": [
    "MM",
    "CM",
    "METER",
    "KM",
    "INCH",
    "FOOT",
    "YARD",
    "MILE",
  ],
  Area: [
    "SQ_MM",
    "SQ_CM",
    "SQ_METER",
    "SQ_KM",
    "SQ_INCH",
    "SQ_FOOT",
    "SQ_YARD",
    "SQ_MILE",
    "ACRE",
    "HECTARE",
  ],
  "Weight / Mass": ["MG", "GRAM", "KG", "TON", "OUNCE", "POUND", "QUINTAL"],
  "Volume / Capacity": [
    "ML",
    "LITER",
    "CUBIC_CM",
    "CUBIC_METER",
    "CUBIC_INCH",
    "CUBIC_FOOT",
    "GALLON",
    "BARREL",
  ],
  "Quantity / Count": [
    "PIECE",
    "UNIT",
    "PACK",
    "BOX",
    "DOZEN",
    "BUNDLE",
    "SET",
    "PAIR",
    "CARTON",
    "CASE",
    "PALLET",
  ],
  Time: ["SECOND", "MINUTE", "HOUR", "DAY", "WEEK", "MONTH", "YEAR"],
  "Digital / Data": ["BYTE", "KB", "MB", "GB", "TB"],
  "Energy / Power": [
    "WATT",
    "KILOWATT",
    "MEGAWATT",
    "JOULE",
    "CALORIE",
    "KCAL",
    "VOLT",
    "AMPERE",
  ],
  Finance: ["PERCENT", "CURRENCY", "POINT"],
  Miscellaneous: [
    "SHEET",
    "ROLL",
    "STRIP",
    "LENGTH",
    "WEIGHT",
    "VOLUME",
    "AREA",
    "SIZE",
  ],
  Other: ["OTHER"],
};

// Component for unit dropdown with search
const UnitDropdown = ({ name, value, onChange, className = "form-input" }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Flatten all units with their categories for searching
  const allUnits = Object.entries(UNIT_CATEGORIES).flatMap(
    ([category, units]) => units.map((unit) => ({ unit, category })),
  );

  // Filter units based on search
  const filteredUnits = searchTerm
    ? allUnits.filter(
        ({ unit, category }) =>
          unit.toLowerCase().includes(searchTerm.toLowerCase()) ||
          category.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : allUnits;

  // Group filtered units by category
  const groupedFilteredUnits = filteredUnits.reduce(
    (acc, { unit, category }) => {
      if (!acc[category]) acc[category] = [];
      acc[category].push(unit);
      return acc;
    },
    {},
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectUnit = (unit) => {
    onChange({ target: { name, value: unit } });
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div
      className="unit-dropdown-wrapper"
      ref={dropdownRef}
      style={{ position: "relative" }}
    >
      <div
        className={className}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "white",
        }}
      >
        <span>{value ? value.replace(/_/g, " ") : "Select Unit"}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: "4px",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "8px",
            maxHeight: "300px",
            overflow: "hidden",
            zIndex: 1000,
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
            <input
              type="text"
              placeholder="Search units..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>

          <div style={{ maxHeight: "240px", overflowY: "auto" }}>
            {Object.keys(groupedFilteredUnits).length > 0 ? (
              Object.entries(groupedFilteredUnits).map(([category, units]) => (
                <div key={category}>
                  <div
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#f5f5f5",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#666",
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    {category}
                  </div>
                  {units.map((unit) => (
                    <div
                      key={unit}
                      onClick={() => handleSelectUnit(unit)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        backgroundColor: value === unit ? "#e3f2fd" : "white",
                        fontSize: "14px",
                      }}
                      onMouseEnter={(e) => {
                        if (value !== unit)
                          e.target.style.backgroundColor = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        if (value !== unit)
                          e.target.style.backgroundColor = "white";
                      }}
                    >
                      {unit.replace(/_/g, " ")}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div
                style={{ padding: "12px", textAlign: "center", color: "#999" }}
              >
                No units found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RawMaterials = ({ markPageAsCompleted }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const [selectedRawMaterials, setSelectedRawMaterials] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [brands, setBrands] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [editableRawMaterials, setEditableRawMaterials] = useState([]);
  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // ---- Sidebar collapse (whole aside) — persisted so it stays how the
  // user left it across reloads -------------------------------------
  const SIDEBAR_COLLAPSED_KEY = "raw_materials_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable (e.g. private mode) — fail silently
      }
      return next;
    });
  };

  // ---- "How It Works" card collapse — independent of the sidebar
  // collapse above, also persisted -------------------------------------
  const INFO_CARD_COLLAPSED_KEY = "raw_materials_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable — fail silently
      }
      return next;
    });
  };

  const { user } = useAuth();

  // Pricing related states
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedMaterialForPricing, setSelectedMaterialForPricing] =
    useState(null);
  const [pricingCombinations, setPricingCombinations] = useState([]);
  const [editablePricingCombinations, setEditablePricingCombinations] =
    useState([]);
  const [pricingUpdateEnabled, setPricingUpdateEnabled] = useState(false);
  const [materialsPricingData, setMaterialsPricingData] = useState({});

  // Derived, real-data metric stats (no fabricated numbers)
  const materialStats = useMemo(() => {
    const total = rawMaterials.length;
    const active = rawMaterials.filter((m) => m.status === "ENABLED").length;
    const disabled = rawMaterials.filter((m) => m.status === "DISABLED").length;
    const priced = rawMaterials.filter((m) => {
      const pricing = materialsPricingData[m.id];
      return pricing && pricing.length > 0;
    }).length;
    return { total, active, disabled, priced };
  }, [rawMaterials, materialsPricingData]);

  const apiBaseUrlsByRole = {
    "ROLE_ERP-ADMIN": process.env.REACT_APP_API_ERP_ADMIN_URL,
    "ROLE_INVENTORY-MANAGER": process.env.REACT_APP_API_INV_MANAGER_URL,
  };

  const getBaseUrlForUser = (user) => {
    if (!user?.authorities) return null;

    // Pick the first matching role that has a URL
    for (const role of user.authorities) {
      if (apiBaseUrlsByRole[role]) return apiBaseUrlsByRole[role];
    }

    return null; // no access
  };

  const baseUrl = getBaseUrlForUser(user);

  const [initialValues, setInitialValues] = useState({
    id: 0,
    name: "",
    description: "",
    brandId: "",
    unit: "",
    status: "ENABLED",
    specifications: [
      {
        id: 0,
        specName: "",
        values: [
          {
            id: 0,
            value: "",
          },
        ],
      },
    ],
  });

  const allowedRoles = ["ROLE_INVENTORY-MANAGER", "ROLE_ERP-ADMIN"];
  const hasAccess = user?.authorities?.some((role) =>
    allowedRoles.includes(role),
  );

  const fetchRawMaterials = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/raw-materials`);

      const rawMaterialsData =
        response.data?.data?.map((material) => ({
          id: material.id || "N/A",
          materialCode: material.materialCode || "Unknown",
          name: material.name || "",
          description: material.description || "",
          brandId: material.brand?.id || null,
          brandName: material.brand?.name || "N/A",
          unit: material.unit || "",
          status: material.status?.toUpperCase() || "ENABLED",
          currentStock: material.rawMaterialStock?.currentStock || 0,
          stockUnit: material.rawMaterialStock?.unit || "PIECE",
          specifications: material.specifications || [],
        })) || [];

      setRawMaterials(rawMaterialsData);
      setEditableRawMaterials(
        rawMaterialsData.map((material) => ({ ...material })),
      );

      if (rawMaterialsData.length > 0) {
        markPageAsCompleted("Raw Materials");
      }
    } catch (error) {
      console.error("Error fetching raw materials:", error);
    }
  }, [markPageAsCompleted, baseUrl]);

  const fetchAllMaterialsPricing = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/raw-materials/price/combinations/all`,
      );
      const data = response.data?.data || [];

      // Convert array to object with rawMaterialId as key for easy lookup
      const pricingMap = {};
      data.forEach((item) => {
        pricingMap[item.rawMaterialId] = item.combinations;
      });

      setMaterialsPricingData(pricingMap);
    } catch (error) {
      console.error("Error fetching all materials pricing:", error);
    }
  }, [baseUrl]);

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const downloadTemplate = async () => {
    try {
      const url = `${baseUrl}/raw-material/template/crm/download-template`;
      const response = await axiosInstance.get(url, { responseType: "blob" });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(response.data);
      link.download = "raw_material_template.xlsx";
      link.click();
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const url = `${baseUrl}/raw-material/template/crm/upload`;

      await axiosInstance.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Bulk upload successful");
      setShowUploadModal(false);
      setSelectedFile(null);
      fetchRawMaterials();
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const fetchDropdownData = useCallback(async () => {
    try {
      const brandsResponse = await axiosInstance.get(`${baseUrl}/brands`);

      const brandsData =
        brandsResponse.data?.data?.map((brand) => ({
          id: brand.id,
          name: brand.brandName,
          brandName: brand.brandName,
          brandCode: brand.brandCode,
          description: brand.description,
          status: brand.status,
        })) || [];

      setBrands(brandsData);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  }, [baseUrl]);

  const fetchMaterialPricingCombinations = useCallback(
    async (materialId) => {
      try {
        const response = await axiosInstance.get(
          `${baseUrl}/raw-materials/price/${materialId}/combinations`,
        );
        return response.data?.data || [];
      } catch (error) {
        console.error("Error fetching material pricing:", error);
        return [];
      }
    },
    [baseUrl],
  );

  useEffect(() => {
    if (!hasAccess) return;

    fetchRawMaterials();
    fetchDropdownData();
    fetchAllMaterialsPricing();
  }, [
    fetchRawMaterials,
    fetchDropdownData,
    fetchAllMaterialsPricing,
    hasAccess,
  ]);

  useEffect(() => {
    setPricingEnabled(selectedRawMaterials.length === 1);
  }, [selectedRawMaterials]);

  const handleTopEnablePricing = async () => {
    const materialId = selectedRawMaterials[0];
    const material = rawMaterials.find((m) => m.id === materialId);
    if (!material) return;

    setSelectedMaterialForPricing(material);

    const combinations = await fetchMaterialPricingCombinations(materialId);
    setPricingCombinations(combinations);
    setEditablePricingCombinations(combinations.map((c) => ({ ...c })));

    setPricingUpdateEnabled(true);
    setShowPricingModal(true);
  };

  const handleSelectAll = () => {
    const allMaterialIds = rawMaterials.map((material) => material.id);
    if (selectAll) {
      setSelectedRawMaterials([]);
      setSelectAll(false);
    } else {
      setSelectedRawMaterials(allMaterialIds);
      setSelectAll(true);
    }
  };

  const handleSelectRawMaterial = (materialId) => {
    setSelectedRawMaterials((prev) => {
      if (prev.includes(materialId)) {
        return prev.filter((id) => id !== materialId);
      } else {
        return [...prev, materialId];
      }
    });
  };

  useEffect(() => {
    const allMaterialIds = rawMaterials.map((material) => material.id);
    const allSelected =
      allMaterialIds.length > 0 &&
      allMaterialIds.every((id) => selectedRawMaterials.includes(id));
    setSelectAll(allSelected);
  }, [selectedRawMaterials, rawMaterials]);

  const handleBatchDelete = () => {
    if (selectedRawMaterials.length === 0) {
      toast.warning("Please select at least one raw material to delete");
      return;
    }

    setPopupAction(() => async () => {
      try {
        await axiosInstance.delete(`${baseUrl}/raw-materials/bulk`, {
          data: selectedRawMaterials,
        });

        toast.success(
          `Successfully deleted ${selectedRawMaterials.length} raw material(s)`,
        );

        setSelectedRawMaterials([]);
        setSelectAll(false);
        fetchRawMaterials();
        markPageAsCompleted("Raw Materials");
      } catch (error) {
        console.error("Error deleting raw materials:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });

    setShowWarningPopup(true);
  };

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      const hasEmptyCode = editableRawMaterials.some(
        (material) =>
          !material.materialCode || material.materialCode.trim() === "",
      );
      if (hasEmptyCode) {
        toast.error("All material codes must be filled out before submitting.");
        return;
      }

      const payload = editableRawMaterials.map((material) => ({
        id: material.id,
        materialCode: material.materialCode,
        name: material.name,
        description: material.description,
        brandId: material.brandId,
        unit: material.unit || "MM",
        status: material.status.toUpperCase(),
        specifications: material.specifications,
      }));

      axiosInstance
        .put(`${baseUrl}/raw-materials/bulk`, payload)
        .then(() => {
          toast.success("Bulk update submitted successfully");
          setRawMaterials([...editableRawMaterials]);
          markPageAsCompleted("Raw Materials");
        })
        .catch((error) => {
          console.error("Error updating raw materials:", error);
        });
    }

    setBulkUpdateEnabled((prev) => !prev);
  };

  const handleInlineEdit = (materialId, field, value) => {
    setEditableRawMaterials((prev) =>
      prev.map((material) =>
        material.id === materialId ? { ...material, [field]: value } : material,
      ),
    );
  };

  const handleEditRawMaterial = (materialId) => {
    const material = rawMaterials.find((m) => m.id === materialId);
    if (material) {
      setIsEditing(true);

      setInitialValues({
        id: material.id,
        name: material.name,
        description: material.description,
        brandId: material.brandId || "",
        unit: material.unit || "",
        status: material.status || "ENABLED",
        specifications:
          material.specifications.length > 0
            ? material.specifications
            : [
                {
                  id: null,
                  specName: "",
                  values: [{ id: null, value: "" }],
                },
              ],
      });

      setShowForm(true);
    }
  };

  const handleDelete = async (materialId) => {
    try {
      await axiosInstance.delete(`${baseUrl}/raw-materials/${materialId}`);
      toast.success("Raw material deleted successfully.");
      fetchRawMaterials();
      markPageAsCompleted("Raw Materials");
    } catch (error) {
      console.error("Error deleting raw material:", error);
    }
  };

  const toggleMaterialStatus = async (materialId, currentStatus) => {
    const isCurrentlyEnabled = currentStatus === "ENABLED";
    const newStatus = isCurrentlyEnabled ? "DISABLED" : "ENABLED";
    if (!baseUrl) return;

    const action = isCurrentlyEnabled ? "disable" : "enable";
    const endpoint = `${baseUrl}/raw-materials/${materialId}/${action}`;

    try {
      await axiosInstance.put(endpoint);

      const updateState = (prevState) =>
        prevState.map((material) =>
          material.id === materialId
            ? { ...material, status: newStatus }
            : material,
        );

      setRawMaterials(updateState);
      setEditableRawMaterials(updateState);

      toast.success(
        `Raw material status changed to ${newStatus.toLowerCase()}`,
      );
      markPageAsCompleted("Raw Materials");
    } catch (error) {
      console.error(`Error changing status to ${newStatus}:`, error);
    }
  };

  // Handle opening pricing modal
  const handleUpdatePricing = async (materialId) => {
    const material = rawMaterials.find((m) => m.id === materialId);
    if (!material) return;

    setSelectedMaterialForPricing(material);

    const combinations = await fetchMaterialPricingCombinations(materialId);
    setPricingCombinations(combinations);

    // Always editable when user clicks Update Pricing directly
    setEditablePricingCombinations(combinations.map((c) => ({ ...c })));
    setPricingUpdateEnabled(true);

    setShowPricingModal(true);
  };

  // Handle inline pricing edit
  const handlePricingInlineEdit = (combinationId, field, value) => {
    setEditablePricingCombinations((prev) =>
      prev.map((combo) =>
        combo.combinationId === combinationId
          ? {
              ...combo,
              [field]:
                field === "available"
                  ? value
                  : field === "hsnCode" ||
                      field === "skuCode" ||
                      field === "minimumStockQuantity"
                    ? value
                    : field === "costPrice"
                      ? value === ""
                        ? ""
                        : parseFloat(value)
                      : field === "minimumStockQuantity"
                        ? value === ""
                          ? ""
                          : parseInt(value)
                        : parseFloat(value) || 0,
            }
          : combo,
      ),
    );
  };

  // Copy pricing to all combinations
  const handleCopyPricingToAll = () => {
    if (editablePricingCombinations.length === 0) return;

    const firstCombo = editablePricingCombinations[0];
    const { costPrice, hsnCode, minimumStockQuantity } = firstCombo;

    setEditablePricingCombinations((prev) =>
      prev.map((combo) => ({
        ...combo,
        costPrice,
        hsnCode: hsnCode || "",
        minimumStockQuantity: minimumStockQuantity || 0,
      })),
    );

    toast.success(
      "Pricing, HSN Code, and Min Stock Quantity copied to all combinations",
    );
  };

  // Submit pricing updates
  const handleSubmitPricing = async () => {
    if (!selectedMaterialForPricing) return;

    // Validate that all SKU codes are filled
    const missingSkus = editablePricingCombinations.filter(
      (combo) => !combo.skuCode || combo.skuCode.trim() === "",
    );

    if (missingSkus.length > 0) {
      toast.error("All SKU codes must be filled before submitting.");
      return;
    }

    // Validate that all cost prices are filled and greater than 0
    const missingPrices = editablePricingCombinations.filter(
      (combo) => !combo.costPrice || combo.costPrice <= 0,
    );

    if (missingPrices.length > 0) {
      toast.error("All cost prices must be filled with values greater than 0.");
      return;
    }

    try {
      const payload = editablePricingCombinations.map((combo) => ({
        combinationId: combo.combinationId,
        specificationValueIds: combo.specificationValues.map(
          (sv) => sv.valueId,
        ),
        hsnCode: combo.hsnCode || "",
        skuCode: combo.skuCode?.trim() || "",
        minimumStockQuantity: parseInt(combo.minimumStockQuantity) || 0,
        costPrice: parseFloat(combo.costPrice) || 0,
        available: combo.available !== false,
      }));

      await axiosInstance.put(
        `${baseUrl}/raw-materials/price/${selectedMaterialForPricing.id}/combinations`,
        payload,
      );

      toast.success("Pricing updated successfully");
      setPricingCombinations([...editablePricingCombinations]);
      setPricingUpdateEnabled(false);
    } catch (error) {
      console.error("Error updating pricing:", error);
    }
  };

  const rawMaterialValidationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    description: Yup.string().notRequired(),
    brandId: Yup.number().required("Brand is required"),
    unit: Yup.string().when([], {
      is: () => !isEditing,
      then: (schema) => schema.required("Unit is required for new materials"),
      otherwise: (schema) => schema.notRequired(),
    }),
    specifications: Yup.array()
      .min(1, "At least one specification is required")
      .of(
        Yup.object().shape({
          specName: Yup.string()
            .trim()
            .required("Specification name is required"),
          values: Yup.array()
            .min(1, "At least one value is required for each specification")
            .of(
              Yup.object().shape({
                value: Yup.string().trim().required("Value is required"),
              }),
            ),
        }),
      ),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setErrors },
  ) => {
    try {
      const payload = {
        id: isEditing ? values.id : 0,
        name: values.name.trim(),
        description: values.description.trim(),
        brandId: parseInt(values.brandId),
        unit: values.unit || "MM",
        status: values.status || "ENABLED",
        specifications: values.specifications
          .filter((spec) => spec.specName && spec.specName.trim())
          .map((spec) => ({
            id: isEditing ? spec.id || 0 : 0,
            specName: spec.specName.trim(),
            values: spec.values
              .filter((val) => val.value && val.value.trim())
              .map((val) => ({
                id: isEditing ? val.id || 0 : 0,
                value: val.value.trim(),
              })),
          }))
          .filter((spec) => spec.values.length > 0),
      };

      if (isEditing) {
        await axiosInstance.put(`${baseUrl}/raw-materials`, payload);
        toast.success("Raw material updated successfully.");
      } else {
        await axiosInstance.post(`${baseUrl}/raw-materials`, payload);
        toast.success("Raw material created successfully.");
      }

      fetchRawMaterials();
      resetForm();
      setIsEditing(false);
      setShowForm(false);
      markPageAsCompleted("Raw Materials");
    } catch (error) {
      console.error("Error submitting raw material:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateMaterialClick = () => {
    if (showForm) {
      setShowForm(false);
    } else {
      setInitialValues({
        id: 0,
        name: "",
        description: "",
        brandId: "",
        unit: "",
        status: "ENABLED",
        specifications: [
          {
            id: 0,
            specName: "",
            values: [{ id: 0, value: "" }],
          },
        ],
      });
      setShowForm(true);
      setIsEditing(false);
    }
  };

  if (!hasAccess) {
    return <AccessDenied />;
  }

  const tableColumns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ColoredCheckbox
            name="select-all"
            color="green"
            checked={selectAll}
            onChange={handleSelectAll}
            value="all"
          />
          <span>Name</span>
        </div>
      ),
      accessor: "nameWithCheckbox",
    },
    { header: "Description", accessor: "description" },
    { header: "Unit", accessor: "unit" },
    { header: "Brand", accessor: "brandName" },
    { header: "Pricing Info", accessor: "pricingInfo" }, // Add this line
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const transformedData = (
    bulkUpdateEnabled ? editableRawMaterials : rawMaterials
  ).map((material) => ({
    nameWithCheckbox: (
      <div className="material-name-cell">
        <ColoredCheckbox
          name={`select-${material.id}`}
          color="green"
          checked={selectedRawMaterials.includes(material.id)}
          onChange={() => handleSelectRawMaterial(material.id)}
          value={material.id}
        />
        <span className="material-avatar" aria-hidden="true">
          {(material.name || "?").charAt(0).toUpperCase()}
        </span>
        {bulkUpdateEnabled ? (
          <input
            type="text"
            value={material.name}
            onChange={(e) =>
              handleInlineEdit(material.id, "name", e.target.value)
            }
            className="inline-edit-input"
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "4px 8px",
              fontSize: "14px",
              minWidth: "150px",
            }}
          />
        ) : (
          <span className="material-name-text">{material.name}</span>
        )}
      </div>
    ),
    description: material.description || "-",
    unit: bulkUpdateEnabled ? (
      <select
        value={material.unit || ""}
        onChange={(e) => handleInlineEdit(material.id, "unit", e.target.value)}
        className="inline-edit-input"
      >
        <option value="">Select Unit</option>
        {Object.entries(UNIT_CATEGORIES).map(([category, units]) => (
          <optgroup key={category} label={category}>
            {units.map((unit) => (
              <option key={unit} value={unit}>
                {unit.replace(/_/g, " ")}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    ) : (
      material.unit || "-"
    ),
    brandName: material.brandName || "-",
    pricingInfo: (() => {
      const pricing = materialsPricingData[material.id];
      if (!pricing || pricing.length === 0) {
        return <span style={{ color: "#999" }}>No pricing data</span>;
      }

      return (
        <div style={{ fontSize: "12px", maxWidth: "200px" }}>
          <div style={{ fontWeight: "600", marginBottom: "4px" }}>
            {pricing.length} combination{pricing.length > 1 ? "s" : ""}
          </div>
          {pricing.slice(0, 2).map((combo, idx) => (
            <div
              key={idx}
              style={{
                padding: "4px",
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                marginBottom: "2px",
              }}
            >
              <div style={{ fontSize: "11px", color: "#666" }}>
                SKU: {combo.skuCode || "-"}
              </div>
              <div style={{ fontSize: "11px" }}>
                ₹{combo.costPrice?.toFixed(2) || "0.00"} | Min Stock:{" "}
                {combo.minimumStockQuantity || "0"}
              </div>
            </div>
          ))}
          {pricing.length > 2 && (
            <div style={{ color: "#666", fontSize: "11px", marginTop: "4px" }}>
              +{pricing.length - 2} more...
            </div>
          )}
        </div>
      );
    })(),
    status: (
      <div className="material-status-cell">
        <ToggleButton
          isOn={material.status === "ENABLED"}
          onToggle={() => toggleMaterialStatus(material.id, material.status)}
          status={material.status === "DISABLED"}
        />
        <span
          className={`material-status-caption ${
            material.status === "ENABLED" ? "is-enabled" : "is-disabled"
          }`}
        >
          {material.status === "ENABLED"
            ? "Available for use"
            : "Hidden from selection"}
        </span>
      </div>
    ),
    actions: !bulkUpdateEnabled && (
      <div style={{ display: "flex", gap: "8px" }}>
        <IconButton
          icon="iconamoon:edit-light"
          onClick={() => handleEditRawMaterial(material.id)}
          variant="edit"
          title="Edit Raw Materials"
        />
        <IconButton
          icon="solar:money-bag-linear"
          onClick={() => handleUpdatePricing(material.id)}
          variant="primary"
          title="Update Pricing"
        />
        <IconButton
          icon="fluent:delete-28-regular"
          onClick={() => handleDelete(material.id)}
          variant="delete"
          title="Delete Raw Materials"
        />
      </div>
    ),
    originalMaterial: material,
    searchableText: `${material.materialCode} ${material.name} ${material.brandName} ${material.status} ${material.unit}`,
  }));

  return (
    <div className="raw-materials">
      {/* Metric summary tiles - derived from real raw material data */}
      <div className="raw-materials__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Materials</span>
            <span className="metric-tile__value">{materialStats.total}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Active Materials</span>
            <span className="metric-tile__value">{materialStats.active}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Disabled Materials</span>
            <span className="metric-tile__value">{materialStats.disabled}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="m9 9 6 6M15 9l-6 6" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Priced Materials</span>
            <span className="metric-tile__value">{materialStats.priced}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M6 3.5a.5.5 0 0 1 .5-.5H11a.5.5 0 0 1 0 1H8.35a3 3 0 0 1 2.53 2H11a.5.5 0 0 1 0 1h-.84a3 3 0 0 1-2.8 2H6.907l4.147 4.146a.5.5 0 0 1-.708.708l-5-5A.5.5 0 0 1 5.5 9h2a2 2 0 0 0 1.937-1.5H6.5a.5.5 0 0 1 0-1h2.937A2 2 0 0 0 6.5 4H6a.5.5 0 0 1-.5-.5" />
            </svg>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="raw-materials__tip-banner">
        <span className="raw-materials__tip-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
            />
          </svg>
        </span>
        <p>
          <strong>Tip:</strong> Set pricing for a material before assigning it
          to production. Disabling a material hides it from new selections but
          keeps its existing stock and pricing data intact.
        </p>
      </div>

      <div className="raw-materials__actions">
        <button
          type="button"
          className={`action-card ${showForm ? "action-card--active" : ""}`}
          onClick={handleCreateMaterialClick}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={showForm ? "M6 18 18 6M6 6l12 12" : "M12 4v16m8-8H4"}
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {showForm ? "Close Material Form" : "Create Material"}
            </span>
            <span className="action-card-desc">
              {showForm ? "Hide the material form" : "Add a new raw material"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${bulkUpdateEnabled ? "action-card--success" : ""}`}
          onClick={handleBulkUpdateClick}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {bulkUpdateEnabled ? "Submit Bulk Update" : "Enable Bulk Update"}
            </span>
            <span className="action-card-desc">
              {bulkUpdateEnabled
                ? "Save changes made to multiple materials"
                : "Edit multiple materials "}
            </span>
          </span>
        </button>

        {bulkUpdateEnabled && (
          <button
            type="button"
            className="action-card action-card--danger-outline"
            onClick={() => {
              setBulkUpdateEnabled(false);
              setEditableRawMaterials([...rawMaterials]); // revert changes
            }}
          >
            <span className="action-card-copy">
              <span className="action-card-title">Cancel Bulk Update</span>
              <span className="action-card-desc">Discard unsaved changes</span>
            </span>
          </button>
        )}

        <button
          type="button"
          className="action-card"
          onClick={() => setShowUploadModal(true)}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 16V4m0 0 4 4m-4-4-4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">Upload Excel</span>
            <span className="action-card-desc">Bulk import materials</span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${!pricingEnabled ? "action-card--disabled" : ""}`}
          onClick={handleTopEnablePricing}
          disabled={!pricingEnabled}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M6 3.5a.5.5 0 0 1 .5-.5H11a.5.5 0 0 1 0 1H8.35a3 3 0 0 1 2.53 2H11a.5.5 0 0 1 0 1h-.84a3 3 0 0 1-2.8 2H6.907l4.147 4.146a.5.5 0 0 1-.708.708l-5-5A.5.5 0 0 1 5.5 9h2a2 2 0 0 0 1.937-1.5H6.5a.5.5 0 0 1 0-1h2.937A2 2 0 0 0 6.5 4H6a.5.5 0 0 1-.5-.5" />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">Enable Pricing Update</span>
            <span className="action-card-desc">
              {pricingEnabled
                ? "Edit pricing for the selected material"
                : "Select exactly one material to enable"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card action-card--danger-outline ${
            selectedRawMaterials.length === 0 ? "action-card--disabled" : ""
          }`}
          onClick={handleBatchDelete}
          disabled={selectedRawMaterials.length === 0}
        >
          <span className="action-card-copy">
            <span className="action-card-title">
              Bulk Delete ({selectedRawMaterials.length})
            </span>
            <span className="action-card-desc">
              Permanently delete selected materials
            </span>
          </span>
        </button>
      </div>

      <div
        className={`raw-materials__layout ${
          isSidebarCollapsed ? "raw-materials__layout--collapsed" : ""
        }`}
      >
        <div className="raw-materials__main">
          {showForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={rawMaterialValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched }) => (
                  <Form>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {isEditing
                        ? "Edit Raw Material"
                        : "Create New Raw Material"}
                    </h3>

                    <div className="material-form">
                      {/* LEFT SIDE: Basic Information */}
                      <div className="material-form__basic-info">
                        <div className="material-form__grid">
                          <div className="material-form-group">
                            <label className="label">Name *</label>
                            <Field
                              name="name"
                              className={`form-input ${
                                errors.name && touched.name ? "error" : ""
                              }`}
                              placeholder="Enter material name"
                            />
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>

                          <div className="material-form-group">
                            <label className="label">Brand *</label>
                            <Field name="brandId">
                              {({ field, form }) => (
                                <select
                                  {...field}
                                  className={`form-input ${
                                    errors.brandId && touched.brandId
                                      ? "error"
                                      : ""
                                  }`}
                                  onChange={(e) =>
                                    form.setFieldValue(
                                      "brandId",
                                      parseInt(e.target.value) || "",
                                    )
                                  }
                                >
                                  <option value="">Select Brand</option>
                                  {brands.map((brand) => (
                                    <option key={brand.id} value={brand.id}>
                                      {brand.brandName}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </Field>
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="brandId"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>

                          <div className="material-form-group">
                            <label className="label">
                              Unit {!isEditing && "*"}
                            </label>
                            {isEditing && values.unit ? (
                              <div className="form-input-readonly">
                                {values.unit.replace(/_/g, " ")}
                              </div>
                            ) : (
                              <Field name="unit">
                                {({ field, form }) => (
                                  <UnitDropdown
                                    name={field.name}
                                    value={field.value}
                                    onChange={(e) =>
                                      form.setFieldValue(
                                        field.name,
                                        e.target.value,
                                      )
                                    }
                                    className={`form-input ${
                                      errors.unit && touched.unit ? "error" : ""
                                    }`}
                                  />
                                )}
                              </Field>
                            )}
                            <div className="error-placeholder">
                              <ErrorMessage
                                name="unit"
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="material-form-group">
                          <label className="label">Description</label>
                          <Field
                            name="description"
                            as="textarea"
                            rows="3"
                            className={`form-input ${
                              errors.description && touched.description
                                ? "error"
                                : ""
                            }`}
                            placeholder="Enter material description"
                          />
                          <div className="error-placeholder">
                            <ErrorMessage
                              name="description"
                              component="div"
                              className="error-message"
                            />
                          </div>
                        </div>

                        <div className="form-actions">
                          <ButtonWithIcon
                            icon="tdesign:send"
                            text={
                              isEditing ? "Update Material" : "Create Material"
                            }
                            type="submit"
                            className={
                              isEditing ? "edit-button" : "submit-button"
                            }
                          />
                        </div>
                      </div>

                      {/* RIGHT SIDE: Specifications with Fixed Scroll */}
                      <div className="material-form__specifications">
                        <div className="specifications-section">
                          <h4>📋 Specifications</h4>

                          <FieldArray name="specifications">
                            {({ push, remove }) => (
                              <>
                                <div className="specifications-scroll-container">
                                  <div className="specifications-array">
                                    {values.specifications.map(
                                      (spec, specIndex) => (
                                        <div
                                          key={specIndex}
                                          className="spec-item"
                                        >
                                          {/* Spec Name Header */}
                                          <div className="spec-header">
                                            <div className="spec-name-input">
                                              <Field
                                                name={`specifications[${specIndex}].specName`}
                                                className="form-input"
                                                placeholder="Spec name (e.g., Size)"
                                              />
                                            </div>
                                            <div className="spec-delete-btn">
                                              <IconButton
                                                icon="fluent:delete-28-regular"
                                                onClick={() =>
                                                  remove(specIndex)
                                                }
                                                variant="delete"
                                              />
                                            </div>
                                          </div>

                                          {/* Spec Values */}
                                          <div className="spec-values-container">
                                            <FieldArray
                                              name={`specifications[${specIndex}].values`}
                                            >
                                              {({
                                                push: pushValue,
                                                remove: removeValue,
                                              }) => (
                                                <>
                                                  <div className="spec-values-array">
                                                    {spec.values.map(
                                                      (val, valIndex) => (
                                                        <div
                                                          key={valIndex}
                                                          className="spec-value-item-compact"
                                                        >
                                                          <div className="value-input">
                                                            <Field
                                                              name={`specifications[${specIndex}].values[${valIndex}].value`}
                                                              className="form-input"
                                                              placeholder="Value"
                                                              onKeyDown={(
                                                                e,
                                                              ) => {
                                                                if (
                                                                  e.key ===
                                                                  "Enter"
                                                                ) {
                                                                  e.preventDefault();
                                                                  pushValue({
                                                                    id: 0,
                                                                    value: "",
                                                                  });

                                                                  // Auto-focus the new field
                                                                  setTimeout(
                                                                    () => {
                                                                      const inputs =
                                                                        document.querySelectorAll(
                                                                          `input[name^="specifications[${specIndex}].values["]`,
                                                                        );
                                                                      if (
                                                                        inputs.length >
                                                                        0
                                                                      ) {
                                                                        const lastInput =
                                                                          inputs[
                                                                            inputs.length -
                                                                              1
                                                                          ];
                                                                        lastInput.focus();
                                                                      }
                                                                    },
                                                                    30,
                                                                  );
                                                                }
                                                              }}
                                                            />
                                                          </div>
                                                          <div className="value-delete-btn">
                                                            <IconButton
                                                              icon="fluent:delete-28-regular"
                                                              onClick={() =>
                                                                removeValue(
                                                                  valIndex,
                                                                )
                                                              }
                                                              variant="delete"
                                                            />
                                                          </div>
                                                        </div>
                                                      ),
                                                    )}
                                                  </div>
                                                  <ButtonWithIcon
                                                    type="button"
                                                    icon="iconamoon:sign-plus-light"
                                                    text="Add Value"
                                                    onClick={() => {
                                                      pushValue({
                                                        id: 0,
                                                        value: "",
                                                      });
                                                      // Focus the new input after a short delay
                                                      setTimeout(() => {
                                                        const inputs =
                                                          document.querySelectorAll(
                                                            `input[name^="specifications[${specIndex}].values["]`,
                                                          );
                                                        if (inputs.length > 0) {
                                                          inputs[
                                                            inputs.length - 1
                                                          ].focus();
                                                        }
                                                      }, 50);
                                                    }}
                                                    variant="secondary"
                                                    className="add-value-btn"
                                                  />
                                                </>
                                              )}
                                            </FieldArray>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>

                                <ButtonWithIcon
                                  type="button"
                                  icon="iconamoon:sign-plus-light"
                                  text="Add Specification"
                                  onClick={() =>
                                    push({
                                      id: 0,
                                      specName: "",
                                      values: [
                                        {
                                          id: 0,
                                          value: "",
                                        },
                                      ],
                                    })
                                  }
                                  variant="secondary"
                                  className="add-spec-btn"
                                />
                              </>
                            )}
                          </FieldArray>
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          {showUploadModal && (
            <div className="upload-modal-overlay">
              <div className="upload-modal">
                <div className="modal-header">
                  <h3>Bulk Upload Raw Materials</h3>
                  <button
                    className="close-button"
                    onClick={() => setShowUploadModal(false)}
                  >
                    ×
                  </button>
                </div>

                <div className="modal-body">
                  <button
                    className="download-template-button"
                    onClick={downloadTemplate}
                  >
                    Download Template
                  </button>

                  <div className="file-input-container">
                    <label
                      htmlFor="raw-material-file"
                      className="file-upload-label"
                    >
                      {selectedFile ? selectedFile.name : "Choose Excel File"}
                    </label>

                    <input
                      id="raw-material-file"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                  </div>

                  <button
                    className="upload-button"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                  >
                    Upload
                  </button>

                  <button
                    className="cancel-button"
                    onClick={() => setShowUploadModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Modal */}
          {showPricingModal && selectedMaterialForPricing && (
            <div className="pricing-modal-overlay">
              <div className="pricing-modal">
                <div className="pricing-modal-header">
                  <h3>Update Pricing for {selectedMaterialForPricing.name}</h3>
                  <button
                    className="close-button"
                    onClick={() => {
                      setShowPricingModal(false);
                      setSelectedMaterialForPricing(null);
                      setPricingUpdateEnabled(false);
                    }}
                  >
                    ×
                  </button>

                  <div className="pricing-header-actions">
                    {/* Info + Copy button */}
                    <>
                      <div className="pricing-info">
                        <svg
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          className="info-icon"
                        >
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm.93-8.588-.862.49A.502.502 0 0 0 8 7.293v3.002a.5.5 0 0 0 .5.5h.003a.5.5 0 0 0 .5-.5V8.07l1.042-.593A.5.5 0 0 0 10 7V5.5a.5.5 0 0 0-.74-.44zM8 4a1 1 0 1 0 .001 2.001A1 1 0 0 0 8 4z" />
                        </svg>
                        <span>Copy uses the first row pricing</span>
                      </div>

                      <button
                        className="copy-btn"
                        onClick={handleCopyPricingToAll}
                      >
                        Copy Pricing to All
                      </button>
                    </>
                    {/* Submit button */}
                    {pricingUpdateEnabled && (
                      <button
                        className="submit-btn"
                        onClick={handleSubmitPricing}
                      >
                        Submit Pricing
                      </button>
                    )}
                  </div>
                </div>

                <div className="pricing-table-container">
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Combination</th>
                        <th>SKU Code</th>
                        <th>HSN Code</th>
                        <th>Cost Price</th>
                        <th>Min Stock Qty</th>
                        <th>Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pricingUpdateEnabled
                        ? editablePricingCombinations
                        : pricingCombinations
                      ).map((combo, index) => (
                        <tr key={combo.combinationId || index}>
                          <td>
                            <div className="combination-specs">
                              {combo.specificationValues.map((sv, idx) => (
                                <div key={idx} className="spec-value-tag">
                                  <span className="spec-name">
                                    {sv.specificationName}:
                                  </span>
                                  <span className="spec-value">{sv.value}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="text"
                                value={combo.skuCode || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "skuCode",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                placeholder="SKU Code *"
                                required
                              />
                            ) : (
                              <span>{combo.skuCode || "-"}</span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="text"
                                value={combo.hsnCode || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "hsnCode",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                placeholder="HSN Code (Optional)"
                              />
                            ) : (
                              <span>{combo.hsnCode || "-"}</span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="number"
                                value={combo.costPrice || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "costPrice",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                min="0.01"
                                step="0.01"
                                placeholder="0"
                                required
                              />
                            ) : (
                              <span>
                                ₹{combo.costPrice?.toFixed(2) || "0.00"}
                              </span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="number"
                                value={combo.minimumStockQuantity || ""}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "minimumStockQuantity",
                                    e.target.value,
                                  )
                                }
                                className="pricing-input"
                                min="0"
                                step="1"
                                placeholder="0"
                              />
                            ) : (
                              <span>{combo.minimumStockQuantity || "0"}</span>
                            )}
                          </td>
                          <td>
                            {pricingUpdateEnabled ? (
                              <input
                                type="checkbox"
                                checked={combo.available !== false}
                                onChange={(e) =>
                                  handlePricingInlineEdit(
                                    combo.combinationId,
                                    "available",
                                    e.target.checked,
                                  )
                                }
                                className="pricing-checkbox"
                              />
                            ) : (
                              <span
                                className={`availability-badge ${
                                  combo.available !== false
                                    ? "available"
                                    : "unavailable"
                                }`}
                              >
                                {combo.available !== false ? "Yes" : "No"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="raw-materials__table-section">
            {materialStats.total === 0 ? (
              <div className="raw-materials__empty-state">
                <svg
                  className="raw-materials__empty-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
                  />
                </svg>
                <h3>No raw materials found</h3>
                <p>
                  Raw materials are the building blocks of your inventory. To
                  get started:
                </p>
                <ol className="raw-materials__empty-steps">
                  <li>Create your first raw material</li>
                  <li>Enable it so it becomes available</li>
                  <li>Set its pricing before using it in production</li>
                </ol>
                <button
                  type="button"
                  className="action-card action-card--primary"
                  onClick={handleCreateMaterialClick}
                >
                  <span className="action-card-copy">
                    <span className="action-card-title">Create Material</span>
                    <span className="action-card-desc">
                      Add your first raw material
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              <GeneralTable
                title="Raw Materials"
                columns={tableColumns}
                data={transformedData}
                selectedCount={selectedRawMaterials.length}
                showPagination={true}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="raw-materials__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className={`raw-materials__sidebar ${
            isSidebarCollapsed ? "raw-materials__sidebar--collapsed" : ""
          }`}
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>

            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Material</strong>
                    <span>
                      Add a new raw material with brand, unit and
                      specifications.
                    </span>
                  </li>
                  <li>
                    <strong>Set Pricing</strong>
                    <span>
                      Configure cost price and SKU for each specification
                      combination.
                    </span>
                  </li>
                  <li>
                    <strong>Use Everywhere</strong>
                    <span>
                      The material becomes available across inventory and
                      production.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Use clear, descriptive material names</li>
              <li>Fill in pricing before enabling a material</li>
              <li>Keep specification values consistent across materials</li>
              <li>
                Review disabled materials regularly and clean up unused ones
              </li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a raw material is permanent and cannot be undone.
              Products or stock records using a deleted material may need to be
              reassigned.
            </p>
          </div>
        </aside>
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedRawMaterials.length} selected raw material${
          selectedRawMaterials.length === 1 ? "" : "s"
        }? This action is permanent and cannot be undone. Stock and pricing data linked to ${
          selectedRawMaterials.length === 1
            ? "this material"
            : "these materials"
        } will also be removed.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default RawMaterials;
